import React from 'react'
import { useTranslation } from 'react-i18next'
import { ListPlus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { formatMoney, isNegativeMoney, isZeroMoney } from '../lib/money'
import type { CostChange, CostLineView } from '../types'

interface Props { change: CostChange; lines: CostLineView[]; currency: string; locale: string; canEdit: boolean; onEdit: () => void }

const ChangeLinesCard: React.FC<Props> = ({ change, lines, currency, locale, canEdit, onEdit }) => {
  const { t } = useTranslation()
  const money = (v: string) => formatMoney(v, { locale, currency })
  const transfer = change.kind === 'TRANSFER'
  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
        <CardTitle className="text-base">{t('CostControl_change_lines_title')}</CardTitle>
        {canEdit && (
          <Button variant="outline" size="sm" onClick={onEdit}>
            <ListPlus className="h-4 w-4" />
            {t('CostControl_change_lines_edit')}
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {lines.length === 0 && <p className="text-sm text-muted-foreground">{t('CostControl_change_lines_empty')}</p>}
        {lines.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('CostControl_lines_item')}</TableHead>
                <TableHead className="w-44 text-right">{t('CostControl_lines_amount')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lines.map((l) => (
                <TableRow key={l.id}>
                  <TableCell>
                    {l.path && <div className="text-xs text-muted-foreground">{l.path}</div>}
                    <div>{l.nodeCode ? `${l.nodeCode} · ${l.nodeName}` : l.nodeName}</div>
                  </TableCell>
                  <TableCell className={cn('text-right tabular-nums', isNegativeMoney(l.amount) && 'text-destructive')}>{money(l.amount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell className="font-semibold">{t(transfer ? 'CostControl_change_balance' : 'CostControl_change_lines_total')}</TableCell>
                <TableCell className={cn('text-right font-semibold tabular-nums', (transfer ? !isZeroMoney(change.total) : isNegativeMoney(change.total)) && 'text-destructive')}>
                  {money(change.total)}
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        )}
      </CardContent>
    </Card>
  )
}
export default ChangeLinesCard
